import {Component, OnDestroy, OnInit} from '@angular/core';
import {ConstantsService} from '../ConstantsService';
import {ConfiguracoesNotaFiscalService} from '../fiscal/services/configuracoes-nota-fiscal.service';
import {FormasDePagamentoService} from '../../services/formas-de-pagamento.service';
import {PagamentosEletronicosService} from '../../services/pagamentos-eletronicos.service';
import {Subscription} from 'rxjs';
import {
  mensagemVinculacaoConnectTef,
  rotuloModalidadeConnectTef,
  rotuloModoOperacaoConnectTef,
  terminalConnectTefFaturavel,
  tipoConnectTefPorMetodo
} from '../../pedidos/pagamentos-eletronicos/pagamentos-eletronicos-apresentacao';

@Component({
  selector: 'app-configuracao-pagamentos-eletronicos',
  templateUrl: './configuracao-pagamentos-eletronicos.component.html',
  styleUrls: ['./configuracao-pagamentos-eletronicos.component.scss']
})
export class ConfiguracaoPagamentosEletronicosComponent implements OnInit, OnDestroy {
  readonly TIPOS_CONNECTTEF = [10, 20, 30, 60, 99];
  readonly METODOS = ['CREDITO', 'DEBITO', 'PIX', 'VOUCHER', 'OUTRO'];
  empresa: any;
  configuracao: any = {ambiente: 'HOMOLOGACAO', ativo: false, modoConformidadeGo: false};
  diagnostico: any = {erros: [], capacidadesOpcionais: {caixa: false, fiscal: false}};
  configuracaoFiscal: any = null;
  terminais: any[] = [];
  formasDePagamento: any[] = [];
  mapeamentos: any[] = [];
  qrCode: string;
  erro: string;
  mensagemSucesso: string;
  carregando = false;
  salvando = false;
  vinculando = false;
  private atualizacao: any;
  private empresaSubscription: Subscription;

  constructor(private constantsService: ConstantsService,
              private pagamentosEletronicosService: PagamentosEletronicosService,
              private formasDePagamentoService: FormasDePagamentoService,
              private configuracoesNotaFiscalService: ConfiguracoesNotaFiscalService) {
  }

  ngOnInit() {
    this.empresaSubscription = this.constantsService.empresa$.subscribe(empresa => {
      if (!empresa) return;
      this.empresa = empresa;
      this.carregue();
    });
    this.atualizacao = window.setInterval(() => this.atualizeTerminais(), 15000);
  }

  ngOnDestroy() {
    if (this.empresaSubscription) this.empresaSubscription.unsubscribe();
    if (this.atualizacao) window.clearInterval(this.atualizacao);
  }

  async carregue() {
    this.carregando = true;
    this.erro = null;
    try {
      const diagnostico = await this.pagamentosEletronicosService.obtenhaDiagnostico(this.configuracao.ambiente);
      this.diagnostico = diagnostico || this.diagnostico;
      this.configuracao = Object.assign(this.configuracao, diagnostico && diagnostico.configuracao || {});
      await Promise.all([this.atualizeTerminais(), this.carregueFormasPagamento(), this.carregueConfiguracaoFiscal()]);
    } catch (erro) {
      this.erro = this.obtenhaMensagemAcionavel(erro);
    } finally {
      this.carregando = false;
    }
  }

  async salveConfiguracao() {
    this.salvando = true;
    this.erro = null;
    try {
      this.configuracao = await this.pagamentosEletronicosService.salveConfiguracao(this.configuracao);
      this.mensagemSucesso = 'Configuração ConnectTEF atualizada.';
      await this.carregue();
    } catch (erro) {
      this.erro = this.obtenhaMensagemAcionavel(erro);
    } finally {
      this.salvando = false;
    }
  }

  async soliciteVinculacao() {
    this.erro = null;
    this.vinculando = true;
    try {
      const vinculacao = await this.pagamentosEletronicosService.soliciteVinculacao();
      this.qrCode = vinculacao && vinculacao.qrCode;
      this.mensagemSucesso = mensagemVinculacaoConnectTef(vinculacao);
      if (vinculacao && vinculacao.erro) this.erro = this.obtenhaMensagemAcionavel(vinculacao.erro);
    } catch (erro) {
      this.erro = this.obtenhaMensagemAcionavel(erro);
    } finally {
      this.vinculando = false;
    }
  }

  get qrCodeImagem() {
    if (!this.qrCode) return null;
    return /^(data:|https?:\/\/|\/)/i.test(this.qrCode) ?
      this.qrCode : `/api/qrcode?c=${encodeURIComponent(this.qrCode)}`;
  }

  async desativeTerminal(terminal: any) {
    this.erro = null;
    try {
      await this.pagamentosEletronicosService.desativeTerminal(terminal.id);
      this.mensagemSucesso = 'Terminal desativado. A próxima recorrência será reduzida; não há crédito retroativo.';
      await this.atualizeTerminais();
    } catch (erro) {
      this.erro = this.obtenhaMensagemAcionavel(erro);
    }
  }

  async salveMapeamentos() {
    this.salvando = true;
    this.erro = null;
    try {
      await this.pagamentosEletronicosService.salveFormasPagamento(this.mapeamentos.map(item => ({
        formaDePagamentoId: item.formaDePagamento.id,
        metodo: item.metodo,
        tiposConnectTef: this.normalizeTiposConnectTef(item.tiposConnectTef).join(','),
        quantidadeMaximaParcelas: 1,
        ativo: item.ativo
      })));
      this.mensagemSucesso = 'De/para de formas de pagamento atualizado.';
      await this.carregueFormasPagamento();
    } catch (erro) {
      this.erro = this.obtenhaMensagemAcionavel(erro);
    } finally {
      this.salvando = false;
    }
  }

  terminalCobravel(terminal: any) {
    return terminalConnectTefFaturavel(terminal);
  }

  rotuloModoOperacao(terminal: any) {
    return rotuloModoOperacaoConnectTef(terminal && terminal.modoOperacao);
  }

  normalizeTiposConnectTef(tipos: any): number[] {
    const valores = Array.isArray(tipos) ? tipos : String(tipos || '').split(',');
    return valores.map(tipo => Number(tipo)).filter(tipo => this.TIPOS_CONNECTTEF.indexOf(tipo) !== -1);
  }

  get terminaisFaturaveis() {
    return this.terminais.filter(terminal => this.terminalCobravel(terminal));
  }

  get estimativaMensal() {
    return this.terminaisFaturaveis.length * 50;
  }

  get fiscalEnvioAutomatico() {
    return !!this.configuracaoFiscal && this.configuracaoFiscal.enviarAutomaticamente === true;
  }

  atualizeMetodoMapeamento(item: any) {
    if(!item) return;
    item.tiposConnectTef = [tipoConnectTefPorMetodo(item.metodo)];
  }

  rotuloMetodo(metodo: string) {
    return ({
      CREDITO: 'Crédito',
      DEBITO: 'Débito',
      PIX: 'Pix',
      VOUCHER: 'Voucher',
      OUTRO: 'Outro',
    } as any)[metodo] || metodo;
  }

  rotuloModalidade(metodo: string) {
    return rotuloModalidadeConnectTef(metodo);
  }

  private async atualizeTerminais() {
    try {
      this.terminais = await this.pagamentosEletronicosService.listeTerminais() || [];
    } catch (erro) {
      this.erro = this.obtenhaMensagemAcionavel(erro);
    }
  }

  private async carregueFormasPagamento() {
    const [formas, mapeamentos] = await Promise.all([
      this.formasDePagamentoService.obtenhaFormasDePagamentos(this.empresa),
      this.pagamentosEletronicosService.listeFormasPagamento(true)
    ]);
    this.formasDePagamento = formas || [];
    this.mapeamentos = this.formasDePagamento.map(forma => {
      const existente = (mapeamentos || []).find(item => Number(item.formaDePagamento && item.formaDePagamento.id) === Number(forma.id));
      if (existente) return Object.assign({}, existente, {
        formaDePagamento: forma,
        tiposConnectTef: this.normalizeTiposConnectTef(existente.tiposConnectTef),
        quantidadeMaximaParcelas: 1
      });
      return {
        formaDePagamento: forma,
        metodo: 'CREDITO',
        tiposConnectTef: [10],
        quantidadeMaximaParcelas: 1,
        ativo: false
      };
    });
  }

  private async carregueConfiguracaoFiscal() {
    try {
      this.configuracaoFiscal = await this.configuracoesNotaFiscalService.obtenhaConfiguracoesNotaFiscal();
    } catch (erro) {
      this.configuracaoFiscal = null;
    }
  }

  private obtenhaMensagemAcionavel(erro: any) {
    const mensagem = String(erro && erro.erro || erro && erro.message || erro || 'API ConnectTEF inacessível. Tente novamente.');
    if (/certificado/i.test(mensagem)) return 'Certificado ausente ou expirado. Atualize o certificado no ambiente do servidor.';
    if (/documento|CPF|CNPJ/i.test(mensagem)) return 'Documento inválido. Informe um CPF ou CNPJ válido para a vinculação.';
    if (/terminal.*vincul|vincul.*terminal/i.test(mensagem)) return 'Terminal não vinculado. Gere um novo QR e conclua o vínculo no terminal.';
    return `API ConnectTEF inacessível ou indisponível: ${mensagem}`;
  }
}
